import { noop } from 'lodash-es';

import type { AssetsSegmentSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  type AssetsEditSegmentSchemaPartial,
  type AssetsHttpPostAssetsByAssetIdSegmentsHeader,
  type AssetsSegmentSchemaWithId,
  httpDeleteAssetsByAssetIdSegmentsBySegmentId,
  httpPatchAssetsByAssetIdSegmentsBySegmentId,
  httpPostAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  type FilesAttachmentSchemaWithIdNullable,
  httpDeleteAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId,
  httpGetAssetsByAssetIdSegmentsBySegmentIdAttachments,
  httpPatchAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId,
  httpPostAssetsByAssetIdSegmentsBySegmentIdAttachments,
} from '@iconik-io/iconik-api/src/services/files';

import { uploadToSignedUrl } from '../../utils/upload_to_signed_url';
import { updateEntities } from '../entities/slice';
import { errorMessage } from '../messages/thunks';
import { type AppDispatch, createAppThunk } from '../types';
import {
  activateDraftCommentRequest,
  postDraftCommentRequest,
} from './draft_comment';

function getAttachmentFormat(file: File) {
  const extension = file.name.includes('.')
    ? file.name.split('.').pop()
    : undefined;
  return (extension || file.type.split('/')[1] || '').toUpperCase();
}

/** What the segment entity keeps of an attachment: nothing needed to upload. */
function toStoredAttachment(
  attachment: FilesAttachmentSchemaWithIdNullable,
): FilesAttachmentSchemaWithIdNullable {
  const {
    upload_credentials,
    upload_headers,
    upload_method,
    upload_url,
    ...stored
  } = attachment;
  return stored;
}

/**
 * Creates the attachment record, sends the file to the storage behind it and
 * closes the record. Only the storage upload is caught:
 * it is a plain fetch the HTTP error middleware never sees, and its record
 * would otherwise stay OPEN.
 */
export const uploadCommentAttachment = createAppThunk(
  (assetId: string, segmentId: string, file: File) =>
    async (dispatch): Promise<FilesAttachmentSchemaWithIdNullable> => {
      const { body: created } = await dispatch(
        httpPostAssetsByAssetIdSegmentsBySegmentIdAttachments(
          assetId,
          segmentId,
          {
            filename: file.name,
            name: file.name,
            format: getAttachmentFormat(file),
            size: file.size,
            content_type: file.type,
          },
        ),
      );

      try {
        await uploadToSignedUrl(created, file);
      } catch (error) {
        dispatch(
          httpDeleteAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId(
            assetId,
            segmentId,
            created.id,
          ),
        );
        dispatch(errorMessage({ text: `Couldn't upload ${file.name}` }));
        throw error;
      }

      const { body: closed } = await dispatch(
        httpPatchAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId(
          assetId,
          segmentId,
          created.id,
          { status: 'CLOSED' },
        ),
      );
      // The create response's url is not a viewing url; the signed one is
      // fetched with the comment's attachments.
      const { url: createdUrl, ...createdRecord } = created;
      return toStoredAttachment({ ...createdRecord, ...closed });
    },
);

export const deleteCommentAttachment = createAppThunk(
  (assetId: string, segmentId: string, attachmentId: string) =>
    async (dispatch): Promise<void> => {
      await dispatch(
        httpDeleteAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId(
          assetId,
          segmentId,
          attachmentId,
        ),
      );
    },
);

/**
 * The comment list carries attachments without urls, so the signed urls are
 * fetched per comment and kept on the segment entity.
 */
export const fetchCommentAttachmentUrls = createAppThunk(
  (assetId: string, segmentId: string) =>
    async (dispatch): Promise<void> => {
      const { body } = await dispatch(
        httpGetAssetsByAssetIdSegmentsBySegmentIdAttachments(
          assetId,
          segmentId,
          { generate_signed_url: true, per_page: 100 },
        ),
      );
      dispatch(
        updateEntities({
          segments: { [segmentId]: { attachments: body.objects } },
        }),
      );
    },
);

/** Whether a send whose files failed can still stop and keep its form. */
export type SendFailureMode = 'abort' | 'publish';

export type SendNewCommentParams = {
  assetId: string;
  segment: AssetsSegmentSchemaPatched;
  files: File[];
  headers?: AssetsHttpPostAssetsByAssetIdSegmentsHeader;
  onFailure: () => SendFailureMode;
};

export type SendCommentEditParams = {
  assetId: string;
  segmentId: string;
  patch: AssetsEditSegmentSchemaPartial;
  files: File[];
  removedIds: string[];
  savedAttachments: FilesAttachmentSchemaWithIdNullable[];
  onFailure: () => SendFailureMode;
  /**
   * Called before an aborted save rejects, with the added files that stayed
   * on the comment because their rollback failed, so a retry skips them.
   */
  onFilesKept?: (files: File[]) => void;
};

type UploadedFile = {
  file: File;
  attachment: FilesAttachmentSchemaWithIdNullable;
};

type Settled<Value> = { done: Value[]; failed: boolean; reason?: unknown };

async function settleAll<Value>(
  promises: Promise<Value>[],
): Promise<Settled<Value>> {
  const settled: Settled<Value> = { done: [], failed: false };
  for (const result of await Promise.allSettled(promises)) {
    if (result.status === 'fulfilled') {
      settled.done.push(result.value);
    } else if (!settled.failed) {
      settled.failed = true;
      settled.reason = result.reason;
    }
  }
  return settled;
}

const hasCommentContent = (segment: AssetsSegmentSchemaPatched) =>
  Boolean(segment.segment_text?.trim()) || Boolean(segment.drawing);

function uploadFiles(
  dispatch: AppDispatch,
  assetId: string,
  segmentId: string,
  files: File[],
) {
  return settleAll(
    files.map((file) =>
      dispatch(uploadCommentAttachment(assetId, segmentId, file)).then(
        (attachment): UploadedFile => ({ file, attachment }),
      ),
    ),
  );
}

/** Deletes the given uploads again and resolves to the ones still there. */
async function rollBackUploads(
  dispatch: AppDispatch,
  assetId: string,
  segmentId: string,
  uploaded: UploadedFile[],
) {
  const results = await Promise.all(
    uploaded.map((upload) =>
      dispatch(
        deleteCommentAttachment(assetId, segmentId, upload.attachment.id),
      ).then(
        () => undefined,
        () => upload,
      ),
    ),
  );
  return results.filter((upload) => upload !== undefined);
}

function discardDraft(dispatch: AppDispatch, assetId: string, draftId: string) {
  // A draft that is never published is purged by the backend after 3 days.
  return dispatch(
    httpDeleteAssetsByAssetIdSegmentsBySegmentId(assetId, draftId),
  ).catch(noop);
}

/**
 * Creates a comment. With files it is created AWAITED, so nobody sees it
 * before its files are in, and published once they are.
 */
export const sendNewComment = createAppThunk(
  ({ assetId, segment, files, headers, onFailure }: SendNewCommentParams) =>
    async (dispatch): Promise<AssetsSegmentSchemaWithId | undefined> => {
      if (files.length === 0) {
        const { body } = await dispatch(
          httpPostAssetsByAssetIdSegments(assetId, segment, {}, headers),
        );
        return body;
      }

      const { body: draft } = await dispatch(
        postDraftCommentRequest(assetId, segment, headers),
      );
      const uploads = await uploadFiles(dispatch, assetId, draft.id, files);
      if (uploads.failed && onFailure() === 'abort') {
        await discardDraft(dispatch, assetId, draft.id);
        throw uploads.reason;
      }
      if (uploads.done.length === 0 && !hasCommentContent(segment)) {
        await discardDraft(dispatch, assetId, draft.id);
        return undefined;
      }

      try {
        const { body } = await dispatch(
          activateDraftCommentRequest(assetId, draft.id, segment),
        );
        dispatch(
          updateEntities({
            segments: {
              [draft.id]: {
                has_attachments: uploads.done.length > 0,
                attachments: uploads.done.map(({ attachment }) => attachment),
              },
            },
          }),
        );
        return { ...body, id: draft.id };
      } catch (error) {
        await discardDraft(dispatch, assetId, draft.id);
        throw error;
      }
    },
);

/**
 * Saves an edited comment: uploads the added files, deletes the removed
 * ones, then patches the text. With the form still open a failure undoes the
 * uploads of this attempt so a retry starts clean.
 */
export const sendCommentEdit = createAppThunk(
  ({
    assetId,
    segmentId,
    patch,
    files,
    removedIds,
    savedAttachments,
    onFailure,
    onFilesKept,
  }: SendCommentEditParams) =>
    async (dispatch): Promise<void> => {
      const patchText = () =>
        dispatch(
          httpPatchAssetsByAssetIdSegmentsBySegmentId(
            assetId,
            segmentId,
            patch,
            { index_immediately: true },
          ),
        );
      if (files.length === 0 && removedIds.length === 0) {
        await patchText();
        return;
      }

      const deletedIds: string[] = [];
      const recordAttachments = (added: UploadedFile[]) => {
        const remaining = savedAttachments.filter(
          (attachment) => !deletedIds.includes(attachment.id),
        );
        dispatch(
          updateEntities({
            segments: {
              [segmentId]: {
                has_attachments: remaining.length + added.length > 0,
                attachments: [
                  ...remaining,
                  ...added.map(({ attachment }) => attachment),
                ],
              },
            },
          }),
        );
      };

      const uploads: Settled<UploadedFile> =
        files.length > 0
          ? await uploadFiles(dispatch, assetId, segmentId, files)
          : { done: [], failed: false };
      const abort = async (reason: unknown): Promise<never> => {
        const kept = await rollBackUploads(
          dispatch,
          assetId,
          segmentId,
          uploads.done,
        );
        if (deletedIds.length > 0 || kept.length > 0) {
          recordAttachments(kept);
        }
        if (kept.length > 0) {
          onFilesKept?.(kept.map(({ file }) => file));
        }
        throw reason;
      };
      if (uploads.failed && onFailure() === 'abort') {
        await abort(uploads.reason);
      }

      const deletes = await settleAll(
        removedIds.map((attachmentId) =>
          dispatch(
            deleteCommentAttachment(assetId, segmentId, attachmentId),
          ).then(() => {
            deletedIds.push(attachmentId);
          }),
        ),
      );
      if (deletes.failed && onFailure() === 'abort') {
        await abort(deletes.reason);
      }

      try {
        await patchText();
      } catch (error) {
        if (onFailure() === 'abort') {
          await abort(error);
        }
        recordAttachments(uploads.done);
        throw error;
      }
      recordAttachments(uploads.done);
    },
);
